import type { UseQueryOptions } from '@tanstack/react-query'
import {
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import axios, { type AxiosResponse } from 'axios'
import type {
  BaseParamsVehicleDto,
  CalcAmountResponse,
  CalcMinAmountHouseholdRequest,
  CalcMinAmountRealtyRequest,
  ConfirmContractRequest,
  ConfirmContractResponse,
  CreateContractAdditionallyRequest,
  CreateContractAdditionallyResponse,
  CreateContractRequest,
  CreateContractResponse,
  GetWarningRequest,
  InsuranceParamsDto,
  InsuranceParamsLeasingDto,
  InsuranceParamsPropertyDto,
  InsuranceParamsTravelDto,
  IsContractConfirmedResponse,
  LastWSProps,
  Property,
  SaveVehicleAdditionalInfoRequest,
  SimpleItem,
  Step1CategoryWarning,
  Step1Warning,
  WizardPropertyAdditionalData,
  WizardTravelAdditionalData,
  WizardVehicleAdditionalData,
} from 'BrokerTsLib/src/dtos'
import type { ContractDocType } from 'BrokerTsLib/src/enums'
import type { GenericQueryOptions } from 'BrokerTsLib/src/hooks'


import { Apps } from '@/dtos'
import apiClient from 'BrokerTsLib/src/Utils/apiClient'
import { useUrl } from '.'
import { Apis } from '../appSettings/AppSettings'
import type { Step1FormValues } from '../containers/steps/vehicle/Step1/Step1'
import type { CommonMeetingRecord } from '../dtos/CommonMeetingRecord'
import { useApp } from './useApp'


export const useGetInsuranceParams = (
  paramsId: string,
  options?: GenericQueryOptions<InsuranceParamsDto>,
) => {
  const { lastApp: { app: lastApp } } = useApp()
  const url = useUrl(lastApp == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  const addonUrl = useUrl(Apis.addonWizard)

  if (lastApp == Apps.shortInsurance) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<AxiosResponse<InsuranceParamsDto>, AxiosError>({
      queryKey: ['getShortInsurance', paramsId],
      queryFn: () =>
        axios.get<InsuranceParamsDto>(
          `${url}/GetShortInsurance?id=${paramsId}`,
        ),
      ...options
    })
  }

  if (lastApp == Apps.extra) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useQuery<AxiosResponse<InsuranceParamsDto>, AxiosError>({
      queryKey: ['getInsuranceParams', paramsId],
      queryFn: () =>
        axios.get<InsuranceParamsDto>(
          `${addonUrl}/GetInsuranceParams?paramsId=${paramsId}`,
        ),
      ...options
    })
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQuery<AxiosResponse<InsuranceParamsDto>, AxiosError>({
    queryKey: ['getInsuranceParams', paramsId],
    queryFn: () =>
      axios.get<InsuranceParamsDto>(
        `${url}/GetInsuranceParams?paramsId=${paramsId}`,
      ),
    ...options
  })
}

export const useSaveStep1 = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  if (app == Apps.shortInsurance)
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useMutation({
      mutationFn: (insuranceParams: Step1FormValues) =>
        axios.post<number>(`${url}/SaveShortInsurance`, insuranceParams),
    })

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useMutation({
    mutationFn: (insuranceParams: Step1FormValues) =>
      axios.post<number>(`${url}/SaveStep1`, insuranceParams),
  })
}

export const useSaveInsuranceParams = () => {
  const url = useUrl(Apis.wizard)

  return useMutation({
    mutationFn: (insuranceParams: InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto | InsuranceParamsLeasingDto) =>
      axios.post<number>(`${url}/SaveInsuranceParams`, insuranceParams),
  })
}

export const useSaveBaseParams = (paramsId: string) => {
  const { app } = useApp()
  
  const api = app == Apps.publicPoleas ? Apis.poleasWizard
    : app == Apps.extra ? Apis.addonWizard
      : Apis.wizard

  const url = useUrl(api)

  return useMutation({
    mutationFn: (data: InsuranceParamsDto | InsuranceParamsTravelDto | InsuranceParamsPropertyDto) =>
      axios.post(`${url}/SaveBaseParams?paramsId=${paramsId ?? '0'}`, data),
  })
}

export const useAddVariant = (paramsId: string) => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: BaseParamsVehicleDto | Property) =>
      axios.post(`${url}/AddVariant?paramsId=${paramsId}`, data),
  })
}

export const useGetStep1Warnings = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: GetWarningRequest) =>
      axios.post<Step1Warning[]>(`${url}/GetWarnings`, data),
  })
}

export const useGetStep1CategoryWarnings = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: GetWarningRequest) =>
      axios.post<Step1CategoryWarning[]>(`${url}/GetCategoryWarnings`, data),
  })
}

export const useUpdateInsuranceStartDate = (paramsId: string) => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (date: Date) =>
      axios.post(`${url}/UpdateInsuranceStartDate`, {
        insuranceStartDate: date,
        paramsId,
      }),
  })
}

export const useCreateContractAdditionally = () => {
  const url = useUrl(Apis.wizard)

  return useMutation({
    mutationFn: (request: CreateContractAdditionallyRequest) =>
      axios.post<CreateContractAdditionallyResponse>(
        `${url}/CreateContractAdditionally`,
        request,
      ),
  })
}

export const useUpdateLastWizardStep = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: LastWSProps) => axios.post(`${url}/UpdateLastWizardStep`, data),
    onSuccess: (_, { paramsId, step }) => {
      queryClient.setQueryData(['getInsuranceParams', paramsId], (oldData?: AxiosResponse<InsuranceParamsDto, AxiosError>) => {
        return (oldData && {
          ...oldData,
          data: {
            ...oldData.data,
            generalParams: {
              ...oldData.data.generalParams,
              lastWizardStep: step
            },
          },
        })!
      })
      queryClient.invalidateQueries({
        queryKey: ['getInsuranceParams', paramsId.toString()],
      })
    },
  })
}

type WizardAdditionalData =
  | WizardVehicleAdditionalData
  | WizardTravelAdditionalData
  | WizardPropertyAdditionalData
export const useGetAdditionalData = (
  paramsId: string,
  options?: GenericQueryOptions<WizardAdditionalData>,
) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useQuery({
    queryKey: ['GetAdditionalData', paramsId],
    queryFn: () =>
      axios.get<WizardAdditionalData>(
        `${url}/GetAdditionalData?paramsId=${paramsId ?? ''}`,
      ),
    ...options
  })
}

export const useSaveAdditionalData = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useMutation({
    mutationFn: (data: WizardAdditionalData) =>
      axios.post(`${url}/SaveAdditionalData`, data),
  })
}

export const useGetMeetingRecord = (
  paramsId: string,
  options?: Omit<
    UseQueryOptions<CommonMeetingRecord, unknown>,
    'queryKey' | 'queryFn'
  >,
) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useQuery({
    queryKey: ['getMeetingRecord', paramsId],
    queryFn: () =>
      axios
        .get<CommonMeetingRecord>(
          `${url}/GetMeetingRecord?paramsId=${paramsId ?? ''}`,
        )
        .then((r) => r.data),
    ...options,
  })
}

export const useSaveMeetingRecord = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (meetingRecord: CommonMeetingRecord) =>
      axios
        .post<CommonMeetingRecord>(`${url}/SaveMeetingRecord`, meetingRecord)
        .then((r) => r.data),

    onSuccess(data, variables, context) {
      queryClient.setQueryData(
        ['getMeetingRecord', variables.paramsId],
        () => {
          return data
        },
      )
    }
  })
}

export const useCreateContract = () => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useMutation({
    mutationFn: (request: CreateContractRequest) =>
      axios.post<CreateContractResponse>(`${url}/CreateContract`, request),
  })
}

export const useCancelContract = () => {
  const url = useUrl(Apis.wizard)

  return useMutation({
    mutationFn: (insuranceId: number) =>
      apiClient.post<{ success?: boolean }>(`${url}/CancelContract`, { insuranceId }).then(response => response.data)
  })
}

export const useReactivateContract = () => {
  const url = useUrl(Apis.wizard)

  return useMutation({
    mutationFn: (insuranceId: number) =>
      apiClient.post<{ success?: boolean }>(`${url}/ReactivateContract`, { insuranceId }).then(response => response.data),
  })
}

export const useGetVehicleInspectionDistricts = (
  offerId: string,
  options?: GenericQueryOptions<SimpleItem[]>,
) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useQuery({
    queryKey: ['useGetVehicleInspectionDistricts', offerId],
    queryFn: () =>
      axios.get<SimpleItem[]>(
        `${url}/GetVehicleInspectionDistricts?offerId=${offerId}`,
      ),
    ...options,
  })
}

export const useGetHomeServices = (
  offerId: string,
  city: string,
  options?: GenericQueryOptions<SimpleItem[]>,
) => {
  const { app } = useApp()
  const url = useUrl(app == Apps.publicPoleas ? Apis.poleasWizard : Apis.wizard)

  return useQuery({
    queryKey: ['useGetHomeServices', offerId, city],
    queryFn: () =>
      axios.get<SimpleItem[]>(
        `${url}/GetHomeServices?offerId=${offerId}&city=${city}`,
      ),
    ...options,
  })
}

export const useGetHomeServiceCities = (
  offerId: string,
  options?: GenericQueryOptions<SimpleItem[]>,
) => {
  const url = useUrl(Apis.wizard)

  return useQuery({
    queryKey: ['useGetHomeServiceCities', offerId],
    queryFn: () =>
      axios.get<SimpleItem[]>(
        `${url}/GetHomeServiceCities?offerId=${offerId}`,
      ),
    ...options,
  })
}

export const useGetDocumentTypes = (offerId: number) => {
  const url = useUrl(Apis.wizard)

  return useQuery({
    queryKey: ['documentTypes', offerId],
    queryFn: () =>
      axios.get<{ type: ContractDocType; description: string }[]>(
        `${url}/GetDocumentTypes`,
        {
          params: { offerId },
        },
      ),
  })
}

export const useUploadInsuranceOfferDocument = () => {
  const url = useUrl(Apis.wizard)

  return useMutation({
    mutationFn: (x: {
      data: FormData
      offerId: number
      documentType: ContractDocType
      overwrite: boolean
    }) =>
      axios.post(`${url}/UploadInsuranceOfferDocument`, x.data, {
        params: {
          offerId: x.offerId,
          documentType: x.documentType,
          overwrite: x.overwrite,
        },
      }),
  })
}

export const useConfirmContract = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: ConfirmContractRequest) =>
      axios.post<ConfirmContractResponse>(`${url}/ConfirmContract`, data),
  })
}

export const useGetIsContractConfirmed = (
  paramsId?: string | null,
  options?: GenericQueryOptions<IsContractConfirmedResponse>,
) => {
  const url = useUrl(Apis.wizard)
  return useQuery({
    queryKey: ['useGetIsContractConfirmed', paramsId],
    queryFn: () =>
      axios.get<IsContractConfirmedResponse>(
        `${url}/IsContractConfirmed?paramsId=${paramsId ?? ''}`,
      ),
    ...options,
  })
}



// jen pro majetek
export const useCalcRecommendedAmountHousehold = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: CalcMinAmountHouseholdRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountHousehold`, data),
  })
}

export const useCalcRecommendedAmountRealty = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (data: CalcMinAmountRealtyRequest) =>
      axios.post<CalcAmountResponse>(`${url}/CalcRecommendedAmountRealty`, data),
  })
}


// jen pro vozidla
export const useSaveVehicleAdditionalInfo = () => {
  const url = useUrl(Apis.wizard)
  return useMutation({
    mutationFn: (request: SaveVehicleAdditionalInfoRequest) =>
      axios.post(`${url}/SaveVehicleAdditionalInfo`, request)
  })
}